import { useState, useRef, useEffect, useId } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
  subLabel?: string;
  badge?: string;
}

interface SearchableSelectProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function SearchableSelect({
  label,
  hint,
  error,
  required,
  disabled,
  placeholder = 'Select an option…',
  options,
  value,
  onChange,
  className = '',
}: SearchableSelectProps) {
  const autoId = useId();
  const id = autoId;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  const filteredOptions = options.filter((o) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      o.label.toLowerCase().includes(term) ||
      (o.subLabel && o.subLabel.toLowerCase().includes(term)) ||
      (o.badge && o.badge.toLowerCase().includes(term))
    );
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    } else {
      setSearch('');
    }
  }, [open]);

  return (
    <div className={`space-y-1 ${className}`} ref={containerRef}>
      {label && (
        <label htmlFor={id} className="block text-xs font-medium text-text-secondary">
          {label}
          {required && (
            <span className="text-error ml-0.5" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <div className="relative">
        <button
          id={id}
          type="button"
          disabled={disabled}
          onClick={() => setOpen(!open)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={[
            'w-full flex items-center justify-between gap-2 rounded-lg border bg-surface px-3 py-2 text-left text-sm transition-colors duration-150',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
            error ? 'border-error' : 'border-border hover:border-text-muted',
            disabled
              ? 'bg-bg-subtle text-text-muted cursor-not-allowed opacity-60'
              : 'text-text-primary',
          ].join(' ')}
        >
          <span className="truncate">
            {selectedOption ? (
              <span className="flex items-center gap-2">
                <span className="font-medium text-text-primary">{selectedOption.label}</span>
                {selectedOption.subLabel && (
                  <span className="text-xs text-text-muted">({selectedOption.subLabel})</span>
                )}
              </span>
            ) : (
              <span className="text-text-muted">{placeholder}</span>
            )}
          </span>
          <svg
            className={`w-4 h-4 text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-surface shadow-xl py-1 max-h-64 flex flex-col">
            {/* Search Input */}
            <div className="p-2 border-b border-border">
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="w-full rounded-md border border-border bg-bg-subtle px-2.5 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none"
              />
            </div>

            {/* Options List */}
            <div className="overflow-y-auto max-h-48 py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-text-muted text-center">
                  No matching results found
                </div>
              ) : (
                filteredOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={opt.value === value}
                    data-testid={`select-option-${opt.value}`}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={[
                      'w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors',
                      opt.value === value
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-text-primary hover:bg-bg-subtle',
                    ].join(' ')}
                  >
                    <div className="truncate pr-2">
                      <div className="font-medium text-text-primary">{opt.label}</div>
                      {opt.subLabel && (
                        <div className="text-[10px] text-text-muted">{opt.subLabel}</div>
                      )}
                    </div>
                    {opt.badge && (
                      <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-surface-muted border border-border text-text-muted">
                        {opt.badge}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-error">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
